import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    FormGroup, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { extractErrorMessage } from '../Services/httpClient';
import { getCurrentUserId, ROLE_LABELS, ROLES } from '../Services/AuthService';
import { createUser, getUserHistory, listUsers, resetUserPassword, setUserActive, setUserRoles, userActionLabel } from '../Services/UsersApi';

const ORANGE = '#f77513';
const ALL_ROLES = [ROLES.editor, ROLES.consentEditor, ROLES.approver, ROLES.userAdmin];

const ROLE_HELP = {
    [ROLES.editor]: 'Edita textos, cadências e tetos de rascunhos e pede publicação.',
    [ROLES.consentEditor]: 'Tudo do editor, mais criar tipos e mudar público, categoria, canais e consentimento.',
    [ROLES.approver]: 'Aprova, agenda, rejeita, reverte e desativa. Nunca a própria versão.',
    [ROLES.userAdmin]: 'Cria usuários, atribui perfis e desativa. Também acessa as telas de administração da plataforma.'
};

const RolePicker = ({ value, onChange }) => (
    <FormGroup>
        {ALL_ROLES.map((role) => (
            <Box key={role} sx={{ mb: 0.5 }}>
                <FormControlLabel
                    control={<Checkbox size="small" checked={value.includes(role)} sx={{ '&.Mui-checked': { color: ORANGE } }}
                        onChange={(e) => onChange(e.target.checked ? [...value, role] : value.filter((r) => r !== role))} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{ROLE_LABELS[role]}</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>{ROLE_HELP[role]}</Typography>
            </Box>
        ))}
    </FormGroup>
);

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '');

const UsersPanel = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialog, setDialog] = useState(null);   // { kind: 'create' | 'roles' | 'password' | 'history' | 'active', user? }
    const [form, setForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [history, setHistory] = useState([]);
    const myId = getCurrentUserId();

    const names = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.userName])), [users]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            setUsers(await listUsers());
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const open = (kind, user, initial = {}) => {
        setDialog({ kind, user });
        setForm(initial);
        setDialogError(null);
        if (kind === 'history') {
            setHistory([]);
            getUserHistory(user.id).then(setHistory).catch((err) => setDialogError(extractErrorMessage(err)));
        }
    };

    const close = () => setDialog(null);

    const run = async (action, successMessage) => {
        setIsSaving(true);
        setDialogError(null);
        try {
            await action();
            setSuccess(successMessage);
            close();
            await load();
        } catch (err) {
            setDialogError(extractErrorMessage(err));
        } finally {
            setIsSaving(false);
        }
    };

    const submitCreate = () => run(
        () => createUser({ userName: form.userName?.trim(), email: form.email?.trim(), initialPassword: form.password, roles: form.roles || [] }),
        `Usuário "${form.userName?.trim()}" criado. Ele troca a senha no primeiro acesso.`);
    const submitRoles = () => run(() => setUserRoles(dialog.user.id, form.roles), `Perfis de "${dialog.user.userName}" atualizados. Valem no próximo login dele.`);
    const submitPassword = () => run(() => resetUserPassword(dialog.user.id, form.password),
        `Senha de "${dialog.user.userName}" redefinida. Ele troca a senha no próximo acesso.`);
    const submitActive = () => run(() => setUserActive(dialog.user.id, !dialog.user.isActive),
        dialog.user.isActive ? `"${dialog.user.userName}" desativado.` : `"${dialog.user.userName}" reativado.`);

    const field = (name) => ({ value: form[name] || '', onChange: (e) => setForm((prev) => ({ ...prev, [name]: e.target.value })) });

    return (
        <Box sx={{ maxWidth: 980 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: ORANGE, fontWeight: 'bold', flexGrow: 1 }}>Usuários do backoffice e seus perfis</Typography>
                <Button variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }} onClick={() => open('create', null, { roles: [] })}>
                    Novo usuário
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {isLoading ? <CircularProgress size={24} style={{ color: ORANGE }} /> : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                <TableCell>Usuário</TableCell>
                                <TableCell>Perfis</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 && (
                                <TableRow><TableCell colSpan={4} align="center" style={{ color: '#aaa', padding: '24px' }}>Nenhum usuário</TableCell></TableRow>
                            )}
                            {users.map((user) => (
                                <TableRow key={user.id} sx={{ opacity: user.isActive ? 1 : 0.55 }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.userName}{user.id === myId ? ' (você)' : ''}</Typography>
                                        {user.email && <Typography variant="caption" color="text.secondary">{user.email}</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        {user.roles.length === 0
                                            ? <Typography variant="caption" color="text.secondary">sem perfil</Typography>
                                            : user.roles.map((role) => <Chip key={role} size="small" label={ROLE_LABELS[role] || role} sx={{ mr: 0.5, mb: 0.5 }} />)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={user.isActive ? 'Ativo' : 'Desativado'} color={user.isActive ? 'success' : 'default'} sx={{ mr: 0.5 }} />
                                        {user.mustChangePassword && <Chip size="small" label="Troca de senha pendente" color="warning" variant="outlined" />}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button size="small" style={{ color: ORANGE }} onClick={() => open('roles', user, { roles: [...user.roles] })}>Perfis</Button>
                                        <Button size="small" style={{ color: ORANGE }} onClick={() => open('password', user, { password: '' })}>Senha</Button>
                                        <Button size="small" style={{ color: ORANGE }} disabled={user.id === myId && user.isActive} onClick={() => open('active', user)}>
                                            {user.isActive ? 'Desativar' : 'Reativar'}
                                        </Button>
                                        <Button size="small" style={{ color: ORANGE }} onClick={() => open('history', user)}>Histórico</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!dialog} onClose={close} fullWidth maxWidth={dialog?.kind === 'history' ? 'md' : 'sm'}>
                {dialog?.kind === 'create' && (
                    <>
                        <DialogTitle>Novo usuário</DialogTitle>
                        <DialogContent>
                            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                            <TextField label="Usuário" fullWidth size="small" sx={{ mt: 1, mb: 2 }} helperText="De 3 a 64 caracteres: letras, números, ponto, hífen, sublinhado ou @." autoFocus {...field('userName')} />
                            <TextField label="E-mail (opcional)" fullWidth size="small" sx={{ mb: 2 }} {...field('email')} />
                            <TextField label="Senha inicial" type="password" fullWidth size="small" sx={{ mb: 2 }}
                                helperText="A pessoa troca no primeiro acesso. Pelo menos 8 caracteres, com maiúscula, minúscula e número." {...field('password')} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Perfis</Typography>
                            <RolePicker value={form.roles || []} onChange={(roles) => setForm((prev) => ({ ...prev, roles }))} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={close} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitCreate} disabled={isSaving || !form.userName || !form.password} variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }}>Criar</Button>
                        </DialogActions>
                    </>
                )}

                {dialog?.kind === 'roles' && (
                    <>
                        <DialogTitle>Perfis de {dialog.user.userName}</DialogTitle>
                        <DialogContent>
                            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                            <Alert severity="info" sx={{ mb: 2 }}>A mudança vale no próximo login da pessoa.</Alert>
                            <RolePicker value={form.roles || []} onChange={(roles) => setForm((prev) => ({ ...prev, roles }))} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={close} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitRoles} disabled={isSaving} variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }}>Salvar</Button>
                        </DialogActions>
                    </>
                )}

                {dialog?.kind === 'password' && (
                    <>
                        <DialogTitle>Redefinir a senha de {dialog.user.userName}</DialogTitle>
                        <DialogContent>
                            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                            <Alert severity="info" sx={{ mb: 2 }}>Você define uma senha provisória e a repassa à pessoa por um canal seguro. Ela é obrigada a trocá-la ao entrar.</Alert>
                            <TextField label="Senha provisória" type="password" fullWidth size="small" autoFocus
                                helperText="Pelo menos 8 caracteres, com maiúscula, minúscula e número." {...field('password')} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={close} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitPassword} disabled={isSaving || !form.password} variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }}>Redefinir</Button>
                        </DialogActions>
                    </>
                )}

                {dialog?.kind === 'active' && (
                    <>
                        <DialogTitle>{dialog.user.isActive ? 'Desativar' : 'Reativar'} {dialog.user.userName}?</DialogTitle>
                        <DialogContent>
                            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                            <Typography variant="body2">
                                {dialog.user.isActive
                                    ? 'A pessoa deixa de conseguir entrar. Quem já está com a sessão aberta pode continuar até o token vencer.'
                                    : 'A pessoa volta a poder entrar com a senha que já tem.'}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={close} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitActive} disabled={isSaving} variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }}>
                                {dialog.user.isActive ? 'Desativar' : 'Reativar'}
                            </Button>
                        </DialogActions>
                    </>
                )}

                {dialog?.kind === 'history' && (
                    <>
                        <DialogTitle>Histórico de {dialog.user.userName}</DialogTitle>
                        <DialogContent>
                            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                            <Table size="small">
                                <TableHead>
                                    <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                        <TableCell>Quando</TableCell><TableCell>O quê</TableCell><TableCell>Quem fez</TableCell><TableCell>Detalhe</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell>{formatDate(event.at)}</TableCell>
                                            <TableCell>{userActionLabel(event.action)}</TableCell>
                                            <TableCell>{names[event.actorId] || event.actorId}</TableCell>
                                            <TableCell>{event.detail}</TableCell>
                                        </TableRow>
                                    ))}
                                    {history.length === 0 && !dialogError && (
                                        <TableRow><TableCell colSpan={4} align="center" style={{ color: '#aaa' }}>Sem registros</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions><Button onClick={close} style={{ color: ORANGE }}>Fechar</Button></DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default UsersPanel;
